// Fliken Regler för automatiska offerter: hur mycket AI får justera priset,
// när en förfrågan går till granskning och om priset visas som intervall.
import { useId, useState, type FormEvent } from 'react';
import { lasHeltal, lasKronor, skrivKronor } from '../../katalog/belopp.js';
import { felText } from '../../katalog/useKatalog.js';
import { useKatalogKontext } from './Katalog.js';

export function Regler() {
  const { katalog, data, kanAndra } = useKatalogKontext();
  const [min, setMin] = useState(String(katalog.aiAdjustmentPercent.min));
  const [max, setMax] = useState(String(katalog.aiAdjustmentPercent.max));
  const [tak, setTak] = useState(skrivKronor(katalog.maxAutoQuoteMonthly));
  const [flaggor, setFlaggor] = useState(katalog.reviewFlags);
  const [nyFlagga, setNyFlagga] = useState('');
  const [intervall, setIntervall] = useState(!katalog.alwaysExactPrice);
  const [fel, setFel] = useState<Record<string, string>>({});
  const [kvitto, setKvitto] = useState(false);
  const id = useId();

  function laggTill() {
    const flagga = nyFlagga.trim();
    if (flagga && !flaggor.includes(flagga)) setFlaggor([...flaggor, flagga]);
    setNyFlagga('');
  }

  async function spara(e: FormEvent) {
    e.preventDefault();
    setKvitto(false);
    const nyttFel: Record<string, string> = {};
    const lagst = lasHeltal(min.replace('−', '-'));
    const hogst = lasHeltal(max.replace('+', ''));
    const belopp = lasKronor(tak);
    if (lagst === null || lagst > 0 || lagst < -100)
      nyttFel.min = 'Skriv ett tal mellan −100 och 0';
    if (hogst === null || hogst < 0 || hogst > 100) nyttFel.max = 'Skriv ett tal mellan 0 och 100';
    if (belopp === null) nyttFel.tak = 'Skriv ett belopp i kronor, till exempel 9 000';
    setFel(nyttFel);
    if (Object.keys(nyttFel).length > 0) return;
    try {
      await data.spara.mutateAsync({
        ...katalog,
        aiAdjustmentPercent: { min: lagst ?? 0, max: hogst ?? 0 },
        maxAutoQuoteMonthly: belopp ?? 0,
        reviewFlags: flaggor,
        alwaysExactPrice: !intervall,
      });
      setKvitto(true);
    } catch {
      // Felet visas nedan.
    }
  }

  return (
    <form className="kort flikkort" onSubmit={(e) => void spara(e)} noValidate>
      <fieldset className="panel-falt" disabled={!kanAndra || data.spara.isPending}>
        <legend className="visuellt-dold">Regler för automatiska offerter</legend>
        <div className="tre">
          <div className="falt">
            <span className="etikett" id={`${id}-just`}>
              AI får justera priset
            </span>
            <div className="med-enhet" role="group" aria-labelledby={`${id}-just`}>
              <input
                className="in mono tal"
                aria-label="Lägsta justering i procent"
                aria-invalid={fel.min ? true : undefined}
                value={min}
                onChange={(e) => setMin(e.target.value)}
              />
              <span>till</span>
              <input
                className="in mono tal"
                aria-label="Högsta justering i procent"
                aria-invalid={fel.max ? true : undefined}
                value={max}
                onChange={(e) => setMax(e.target.value)}
              />
              <span>%</span>
            </div>
            {(fel.min || fel.max) && <div className="faltfel">{fel.min ?? fel.max}</div>}
          </div>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-tak`}>
              Granska om månadssumma över
            </label>
            <div className="med-enhet">
              <input
                id={`${id}-tak`}
                className="in mono tal"
                inputMode="decimal"
                aria-invalid={fel.tak ? true : undefined}
                value={tak}
                onChange={(e) => setTak(e.target.value)}
              />
              <span>kr</span>
            </div>
            {fel.tak && <div className="faltfel">{fel.tak}</div>}
          </div>
        </div>
        <fieldset className="grupp-falt">
          <legend className="etikett">Granska alltid när förfrågan nämner</legend>
          <ul className="flaggor">
            {flaggor.map((f) => (
              <li key={f} className="chip chip-rod">
                {f}
                <button
                  type="button"
                  className="chip-x"
                  aria-label={`Ta bort ${f}`}
                  onClick={() => setFlaggor(flaggor.filter((x) => x !== f))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="med-enhet">
            <input
              className="in"
              aria-label="Nytt ord eller ämne att granska"
              placeholder="Till exempel företagsköp"
              value={nyFlagga}
              onChange={(e) => setNyFlagga(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  laggTill();
                }
              }}
            />
            <button type="button" className="btn btn-s" onClick={laggTill}>
              Lägg till
            </button>
          </div>
        </fieldset>
        <label className="vaxel">
          <span>
            <span className="val-namn">Visa intervall när volymen är uppskattad</span>
            <span className="hjalp">
              Annars visas ett exakt pris, räknat på AI:s bästa uppskattning
            </span>
          </span>
          <input
            className="sw"
            type="checkbox"
            role="switch"
            checked={intervall}
            onChange={(e) => setIntervall(e.target.checked)}
          />
        </label>
      </fieldset>
      {data.spara.isError && (
        <div className="meddelande" role="alert">
          {felText(data.spara.error)}
        </div>
      )}
      <div className="levande" aria-live="polite">
        {kvitto && <div className="kvitto">Reglerna är sparade i utkastet.</div>}
      </div>
      {kanAndra && (
        <div className="panel-fot slut">
          <button type="submit" className="btn btn-s" disabled={data.spara.isPending}>
            {data.spara.isPending ? 'Sparar …' : 'Spara utkast'}
          </button>
        </div>
      )}
    </form>
  );
}
